import './detaile/Claimstyle.less';
import styled from 'styled-components';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Form, Row, Button, Col, Modal, Checkbox, Divider } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PostClaimCIData } from 'all-in-one-shared-api';
import { revalidateAntdFormLanguageChanged } from '../../i18n';
import GreetingSection from '../../components/navbar/GreetingSection';
import { DocumentsCardForCI, ClaimformForCI, CIPart3 } from './components';
import { CommonTnc } from 'components/CommonTncBn';
import calendar from 'assets/images/MakeClaim/calendar.svg';
import { CheckboxChangeEvent } from 'antd/es/checkbox';

import { useLocation } from 'react-router-dom';

import { extractFilesForHistory, getCachedFiles, clearClaimFilesCache } from './claimFilesCache';

type ClaimFormInput = PostClaimCIData;

interface LocationState {
  policyNo?: string;
  formData?: ClaimFormInput;
  currentStep?: number;
  settlementOptionShowAll?: boolean;
  chequeCurrencyOptions?: { value: string; desc?: string; descTC?: string }[];
}
//内容部分布局===========================================================================
const ClaimPageContainer = styled.div`
  margin: 0;
`;
//=====================================================================================
function MakeClaimPage() {
  //定义变量=========================================================
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  // Keep settlementOptionShowAll in sync with location.state so it survives
  // back navigation from ClaimConfirmPage (component remounts on return).
  const updateSettlementOptionShowAll = useCallback(
    (value: boolean) => {
      setSettlementOptionShowAll(value);
      navigate('.', {
        replace: true,
        state: {
          ...(location.state as LocationState),
          settlementOptionShowAll: value,
        },
      });
    },
    [navigate, location.state],
  );
  //定义表单form
  const [form] = Form.useForm<ClaimFormInput>();
  //定义表单上传文件
  //定义绑定值
  // const [isAgreed, setIsAgreed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [enquiryReference, setEnquiryReference] = useState('');
  const [documentInvalid, setDocumentInvalid] = useState(false);
  const [isAgreed, setIsAgreed] = useState(false);
  const [currentStep, setCurrentStep] = useState(1); // 1: 填写表单, 2: 上传文件
  const [settlementOptionShowAll, setSettlementOptionShowAll] = useState(
    (location.state as LocationState)?.settlementOptionShowAll ?? false,
  );
  // Keep chequeCurrencyOptions in sync with location.state so it survives
  // back navigation from ClaimConfirmPage (component remounts on return).
  const [chequeCurrencyOptions, setChequeCurrencyOptions] = useState<
    { value: string; desc?: string; descTC?: string }[]
  >((location.state as LocationState)?.chequeCurrencyOptions ?? []);
  const updateChequeCurrencyOptions = useCallback(
    (options: { value: string; desc?: string; descTC?: string }[]) => {
      setChequeCurrencyOptions(options);
      navigate('.', {
        replace: true,
        state: {
          ...(location.state as LocationState),
          chequeCurrencyOptions: options,
        },
      });
    },
    [navigate, location.state],
  );

  // 当 settlementOptionShowAll 变化后，若列表不再包含 "Accumulate in Benefit Accumulation Account"，清除用户之前已选择的该结算选项
  useEffect(() => {
    if (!settlementOptionShowAll) {
      const currentSettlement = form.getFieldValue('settlementOptions');
      if (currentSettlement === 'Accumulate in Benefit Accumulation Account') {
        form.setFieldsValue({ settlementOptions: undefined });
      }
    }
  }, [settlementOptionShowAll, form]);
  const { policyNo, formData: restoredFormData, currentStep: restoredStep } = (location.state as LocationState) ?? {};

  //提交按钮
  const onSubmit = () => {
    setSnackbarMessage('');
    setEnquiryReference('');
    console.log('000000');
    // setTimeout(() => shouldPostEnquirySubmission(true), 500);
    setIsLoading(true);
  };
  // 仅在常规入口（step1）时执行一次：在历史栈底垫一层 ClaimEnquiryPage(tabs:1)，
  // 使 step1 原生返回能回到带 tabs 的理赔查询页（与 step1 cancel 按钮行为一致）
  const didInitHistoryRef = useRef(false);
  useEffect(() => {
    if (didInitHistoryRef.current) return;
    didInitHistoryRef.current = true;
    const initialState = (location.state as LocationState) ?? {};
    // 从 ClaimConfirm 带 currentStep:3 进入等场景不铺底座
    if (!initialState.currentStep || initialState.currentStep === 1) {
      // 新发起理赔，清空历史遗留的上传文件缓存
      clearClaimFilesCache();
      navigate('/ClaimEnquiryPage', { state: { tabs: 1 }, replace: true });
      navigate('/SubmitClaimFormPage', {
        state: { policyNo: initialState.policyNo, currentStep: 1 },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  //监听值useEffect==================================================
  useEffect(() => {
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 50);
  }, [currentStep]);

  useEffect(() => {
    revalidateAntdFormLanguageChanged(form);
  }, [i18n.language, form]);

  const onChange = (e: CheckboxChangeEvent) => {
    setIsAgreed(e.target.checked);
  };
  const restoredRef = useRef(false);
  useEffect(() => {
    if (!restoredRef.current && restoredFormData) {
      // history.state 中只保存了可序列化字段，上传文件需从内存缓存合并还原
      form.setFieldsValue({ ...restoredFormData, ...getCachedFiles() });
      restoredRef.current = true;
    }

    setCurrentStep(restoredStep ?? 1);
  }, [restoredFormData, restoredStep, form]);

  useEffect(() => {
    if (!snackbarMessage) return;
    setIsLoading(false);
  }, [snackbarMessage, isLoading]);
  useEffect(() => {
    if (!enquiryReference) return;
    // shouldPostEnquirySubmission(false);
    setIsLoading(false);
  }, [enquiryReference, setIsLoading]);

  return (
    <ClaimPageContainer className="Claim">
      <GreetingSection pageName={t('life.submitClaims.cardTitle')} showicon={false} />
      <Row justify="center" gutter={[16, { xs: 8, sm: 8, lg: 24 }]} className="ClaimContent">
        <Row>
          <Col xs={24} md={24}>
            {currentStep === 1 && (
              <div className="speakheader">
                <div>
                  <img src={calendar} style={{ margin: '0  10px 5px 0', width: '24px', height: '24px' }} />
                  {t('life.submitClaims.Speakclaim')}
                </div>
              </div>
            )}
          </Col>
        </Row>
        <Col xs={{ span: 24 }} sm={{ span: 20 }} lg={{ span: 24 }}>
          <Row
            justify="center"
            gutter={[16, { xs: 8, sm: 8, lg: 24 }]}
            style={{ background: '#fff' }}
            className="centerlist"
          >
            <Form
              // initialValues={initialValues}
              layout={'vertical'}
              requiredMark={false}
              onFinish={onSubmit}
              form={form}
              validateTrigger={'onSubmit'}
              className="form"
              preserve={true}
              scrollToFirstError={{ behavior: 'smooth', block: 'center' }}
            >
              {/* Step 1: 填写表单 */}
              {currentStep === 1 && (
                <>
                  <ClaimformForCI
                    policyNo={policyNo}
                    onSettlementOptionShowAllChange={updateSettlementOptionShowAll}
                    onChequeCurrencyOptionsChange={updateChequeCurrencyOptions}
                  />
                  <Col xs={24} md={24}>
                    <Row
                      className="ChageBeneficialPage"
                      style={{
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        paddingTop: 16,
                        maxWidth: '640px',
                        margin: '0 auto',
                      }}
                    >
                      <Button
                        onClick={() => {
                          form
                            .validateFields()
                            .then(() => {
                              // const allValues = accumulateFormValues();
                              //  console.log('formData:submit3', allValues);
                              console.log('formData:submit1', form.getFieldsValue(true));
                              setCurrentStep(2);
                              // 将当前步骤与表单数据写入 history，使原生返回可逐级回退并保留已填数据
                              navigate(location.pathname, {
                                state: {
                                  ...((location.state as LocationState) ?? {}),
                                  currentStep: 2,
                                  formData: extractFilesForHistory(form.getFieldsValue(true)),
                                },
                              });
                            })
                            .catch(({ errorFields }) => {
                              // 验证失败，手动滚动到第一个错误字段
                              if (errorFields && errorFields.length > 0) {
                                form.scrollToField(errorFields[0].name, {
                                  behavior: 'smooth',
                                  block: 'center',
                                });
                              }
                            });
                        }}
                        type="primary"
                        className="submit"
                      >
                        {t('life.MakeRequest.contact.next')}
                      </Button>
                      <Button
                        onClick={() => {
                          Modal.confirm({
                            title: t('life.submitClaims.leaveConfirm'),
                            icon: '',
                            width: 510,
                            centered: true,
                            className: 'Modalconfirm Modalconfirm-leave',
                            okText: t('common.ok'),
                            cancelText: t('common.cancel'),
                            onOk: () => {
                              clearClaimFilesCache();
                              form.resetFields();
                              navigate('/ClaimEnquiryPage', { state: { tabs: 1 } });
                            },
                          });
                        }}
                        className="cancel"
                      >
                        {t('common.cancel')}
                      </Button>
                    </Row>
                  </Col>
                </>
              )}

              {/* Step 2: 上传文件 */}
              {currentStep === 2 && (
                <>
                  <DocumentsCardForCI setInvalid={setDocumentInvalid} />
                  <Col xs={24} md={24}>
                    <Row
                      className="ChageBeneficialPage"
                      style={{
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        paddingTop: 16,
                        maxWidth: '640px',
                        margin: '0 auto',
                      }}
                    >
                      <Button
                        onClick={() => {
                          // const allValues = accumulateFormValues();
                          // console.log('formData:submit2', allValues);
                          console.log('formData:submit2', form.getFieldsValue(true));
                          setCurrentStep(3);
                          navigate(location.pathname, {
                            state: {
                              ...((location.state as LocationState) ?? {}),
                              currentStep: 3,
                              formData: extractFilesForHistory(form.getFieldsValue(true)),
                            },
                          });
                        }}
                        disabled={documentInvalid}
                        type="primary"
                        className="submit"
                      >
                        {t('life.MakeRequest.contact.next')}
                      </Button>
                      <Button
                        onClick={() => {
                          // 返回上一步（与原生返回行为一致）
                          navigate(-1);
                        }}
                        className="cancel"
                      >
                        {t('common.back')}
                      </Button>
                    </Row>
                  </Col>
                </>
              )}

              {/* Step 3: 协议确认 */}
              {currentStep === 3 && (
                <>
                  <CIPart3
                    settlementOptionShowAll={settlementOptionShowAll}
                    chequeCurrencyOptions={chequeCurrencyOptions}
                  />
                  <Row justify="space-between" className="pointer">
                    <Divider style={{ margin: '0px 0 8px 0', borderColor: '#cccccc' }}></Divider>

                    <Col style={{ fontSize: '17px' }}>
                      <CommonTnc></CommonTnc>
                    </Col>
                  </Row>
                  <Row justify="space-between" className="pointer" style={{ marginTop: '16px' }}>
                    <Col>
                      <span className="font-size17">{t('life.submitClaims.accepttext2')}</span>
                    </Col>
                  </Row>
                  <Row justify="space-between" className="pointer" style={{ marginTop: '16px' }}>
                    <Col>
                      <Checkbox checked={isAgreed} onChange={onChange} className="font-size17">
                        {t('life.MakeRequest.contact.agreetext')}
                      </Checkbox>
                    </Col>
                  </Row>
                  {/* <AgreementCard setIsAgreed={setIsAgreed} /> */}
                  <Col xs={24} md={24}>
                    <Row
                      className="ChageBeneficialPage"
                      style={{
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        paddingTop: 16,
                        maxWidth: '640px',
                        margin: '0 auto',
                      }}
                    >
                      <Button
                        // loading={enquirySubmissionIsLoading || isLoading}
                        disabled={!isAgreed}
                        onClick={() => {
                          form
                            .validateFields()
                            .then(() => {
                              const fullFormData = form.getFieldsValue(true);
                              navigate(location.pathname, {
                                replace: true,
                                state: {
                                  ...((location.state as LocationState) ?? {}),
                                  currentStep: 3,
                                  formData: extractFilesForHistory(fullFormData),
                                },
                              });
                              navigate('/ClaimConfirmPage', {
                                state: { formData: extractFilesForHistory(fullFormData), updateFiles: 'normal' },
                              });
                            })
                            .catch(({ errorFields }) => {
                              // 验证失败，手动滚动到第一个错误字段
                              if (errorFields && errorFields.length > 0) {
                                form.scrollToField(errorFields[0].name, {
                                  behavior: 'smooth',
                                  block: 'center',
                                });
                              }
                            });
                        }}
                        type="primary"
                        className="submit"
                      >
                        {t('life.MakeRequest.contact.next')}
                      </Button>
                      <Button
                        onClick={() => {
                          // 返回上一步（与原生返回行为一致）
                          navigate(-1);
                        }}
                        className="cancel"
                      >
                        {t('common.back')}
                      </Button>
                    </Row>
                  </Col>
                </>
              )}
            </Form>
          </Row>

          {/* <Row>
          <Col xs={24} md={24}></Col>
        </Row> */}
        </Col>
      </Row>
    </ClaimPageContainer>
  );
}

export default MakeClaimPage;
